/**
 * FAQ estático de Arecore ERP.
 * Mantener sincronizado con los flujos reales del sistema.
 * Última revisión: 2026-05-24
 */

export const SOPORTE_MAIL = 'soporte@arecotech.com';

export const FAQ = [
  // ── General ──────────────────────────────────────────────────────────────
  {
    categoria: 'General',
    preguntas: [
      {
        q: '¿Qué es Arecore ERP?',
        a: 'Arecore es un sistema de gestión comercial (ERP) que centraliza ventas, compras, stock, tesorería y reportes en un solo lugar. Está desarrollado por Arecotech y pensado para negocios que necesitan control completo de su operación.',
      },
      {
        q: '¿Cómo uso el buscador rápido?',
        a: 'Presioná Ctrl+K (o Cmd+K en Mac) desde cualquier pantalla para abrir el buscador. Podés escribir el nombre de un módulo (por ejemplo "clientes") para navegar directo, o elegir acciones rápidas como "Nueva factura de venta". Navegá con las flechas ↑↓ y confirmá con Enter. Cerrá con Escape o haciendo click fuera del panel.',
      },
      {
        q: '¿Cómo activo el modo oscuro?',
        a: 'En la barra superior derecha hay un ícono de sol/luna. Hacé click para alternar entre modo claro y oscuro. La preferencia se guarda automáticamente en el navegador.',
      },
      {
        q: '¿Qué significan los roles de usuario?',
        a: 'Hay dos roles: Administrador tiene acceso completo a todos los módulos, incluyendo configuración y usuarios. Empleado tiene acceso limitado según lo que el administrador configure. El rol se asigna al crear o editar un usuario desde Configuración › Usuarios.',
      },
      {
        q: '¿Puedo tener múltiples usuarios activos al mismo tiempo?',
        a: 'Sí. Cada usuario tiene sus propias credenciales y accede de forma independiente. El sistema registra qué usuario realizó cada operación (ventas, ajustes, movimientos de caja, etc.).',
      },
    ],
  },

  // ── Dashboard ─────────────────────────────────────────────────────────────
  {
    categoria: 'Dashboard',
    preguntas: [
      {
        q: '¿Qué información muestra el Dashboard?',
        a: 'El Dashboard muestra un resumen del día: ventas del día vs. ayer, compras del mes, saldo de cajas, y valor del inventario. También incluye un gráfico de ventas de los últimos 7 días, los 5 productos más vendidos del mes, y los productos con stock bajo o sin stock.',
      },
      {
        q: '¿Por qué aparecen productos en alerta de stock?',
        a: 'Un producto aparece en alerta cuando su stock actual es menor o igual al "stock mínimo" configurado en su ficha. Para desactivar la alerta de un producto, editá su stock mínimo desde Stock › Productos. También podés desactivar las alertas globalmente desde Configuración › Parámetros.',
      },
      {
        q: '¿Las cifras del Dashboard se actualizan en tiempo real?',
        a: 'Se cargan al abrir la pantalla. Para ver los datos más recientes, recargá la página o volvé al Dashboard desde el menú lateral.',
      },
    ],
  },

  // ── Ventas ────────────────────────────────────────────────────────────────
  {
    categoria: 'Ventas',
    preguntas: [
      {
        q: '¿Cuál es la diferencia entre POS y Facturas?',
        a: 'POS (Punto de Venta) es la pantalla optimizada para ventas rápidas en mostrador: agregás productos al carrito, elegís forma de pago y confirmás. Facturas muestra el historial completo de ventas con filtros, detalle y estado. Ambas trabajan sobre la misma base de datos.',
      },
      {
        q: '¿Cómo registro una nueva venta?',
        a: 'Andá a Ventas › POS. Buscá los productos por nombre o SKU y hacé click en "+" para agregarlos al carrito. Podés ajustar la cantidad haciendo click sobre el ítem. Opcionalmente asociá un cliente y aplicá un descuento. Elegí la forma de pago y presioná "Confirmar venta". El stock se descuenta automáticamente.',
      },
      {
        q: '¿Puedo vender sin asociar un cliente?',
        a: 'Sí. El campo de cliente es opcional en el POS. La venta se registra como "consumidor final". Si necesitás facturación o seguimiento por cliente, podés asociarlo buscando por nombre o RUT/CUIT.',
      },
      {
        q: '¿Cómo aplico un descuento?',
        a: 'En el POS, el campo de descuento aparece debajo del subtotal. Ingresá el monto en pesos (no en porcentaje). El descuento se aplica al total de la venta.',
      },
      {
        q: '¿Cómo anulo una venta?',
        a: 'Desde Ventas › Facturas, buscá la venta en el historial y hacé click en el ícono de anulación (X roja). El sistema te pedirá confirmación. Al anular, el stock de los productos vuelve a su valor anterior automáticamente.',
      },
      {
        q: '¿Qué es una cobranza?',
        a: 'Las cobranzas registran cobros pendientes de clientes con deuda. Se accede desde Ventas › Cobranzas. Podés filtrar por cliente y registrar el monto cobrado con fecha y forma de pago.',
      },
    ],
  },

  // ── Compras ───────────────────────────────────────────────────────────────
  {
    categoria: 'Compras',
    preguntas: [
      {
        q: '¿Cómo registro una factura de compra?',
        a: 'Andá a Compras › Facturas y hacé click en "Nueva compra". Seleccioná el proveedor (o dejá en blanco), completá el número de factura, fecha y forma de pago. Luego agregá los ítems con producto, cantidad y precio unitario. Al confirmar, el stock de cada producto se incrementa automáticamente.',
      },
      {
        q: '¿Las compras actualizan el stock automáticamente?',
        a: 'Sí. Al confirmar una compra, el sistema registra un movimiento de stock de tipo "compra" para cada ítem, incrementando el stock_actual del producto. Podés verificarlo en Stock › Movimientos.',
      },
      {
        q: '¿Cómo registro un pago a un proveedor?',
        a: 'Andá a Compras › Pagos y hacé click en "Nuevo pago". Podés asociarlo a un proveedor específico o dejarlo sin asociar. Ingresá el monto, fecha y forma de pago. Opcionalmente podés referenciar el número interno de la compra.',
      },
      {
        q: '¿Puedo anular una compra?',
        a: 'Sí, desde Compras › Facturas. Al anular, el stock agregado por esa compra se revierte automáticamente. No se puede anular una compra si el stock resultante quedaría negativo.',
      },
      {
        q: '¿Qué es un proveedor?',
        a: 'Los proveedores son las empresas o personas de las que comprás mercadería. Se gestionan desde Compras › Proveedores. Podés registrar razón social, RUT/CUIT, teléfono, mail y notas. Los proveedores se asocian a las facturas de compra y a los pagos.',
      },
    ],
  },

  // ── Stock ─────────────────────────────────────────────────────────────────
  {
    categoria: 'Stock',
    preguntas: [
      {
        q: '¿Cómo se actualiza el stock de los productos?',
        a: 'El stock nunca se edita directamente en la ficha del producto. Solo se actualiza a través de movimientos: las compras lo suman, las ventas lo restan, y los ajustes manuales permiten corregir diferencias. Todo queda registrado en el historial de movimientos.',
      },
      {
        q: '¿Cómo agrego o edito un producto?',
        a: 'Andá a Stock › Productos. Para crear uno nuevo, hacé click en "Nuevo producto" e ingresá nombre, SKU (opcional), categoría, unidad, precios de costo y venta, y configuración de stock. Para editar, hacé click en el ícono de lápiz en la fila del producto.',
      },
      {
        q: '¿Qué significa "controla stock"?',
        a: 'Si un producto tiene activado "controla stock", el sistema lleva registro de su cantidad disponible, permite configurar un stock mínimo y genera alertas cuando cae por debajo. Si está desactivado, el producto aparece sin restricciones de disponibilidad (útil para servicios o productos que no se inventarían).',
      },
      {
        q: '¿Qué es el stock mínimo?',
        a: 'Es el nivel de alerta. Cuando el stock actual de un producto es menor o igual al stock mínimo, aparece en rojo en el Dashboard y en el reporte de stock. Configuralo desde la ficha del producto en Stock › Productos.',
      },
      {
        q: '¿Qué es un ajuste de stock?',
        a: 'Un ajuste permite corregir manualmente el stock de un producto. Hay tres tipos: Entrada (suma unidades, por ejemplo devoluciones o errores de conteo), Salida (resta unidades) y Merma (resta unidades por pérdida o vencimiento). Para salidas y mermas, el motivo es obligatorio. Los ajustes se registran desde Stock › Ajustes.',
      },
      {
        q: '¿Qué es una toma de inventario?',
        a: 'Es un conteo físico masivo. Desde Stock › Ajustes › Toma de inventario, se muestra la tabla completa de productos con su stock según el sistema. Ingresás la cantidad física que contaste en cada producto, y el sistema calcula automáticamente la diferencia. Al confirmar, se generan los ajustes necesarios para cada producto con diferencia.',
      },
      {
        q: '¿Para qué sirven los depósitos?',
        a: 'Los depósitos representan ubicaciones físicas de almacenamiento (por ejemplo: "Depósito Central", "Heladera"). Se gestionan desde Stock › Depósitos. Al hacer click en un depósito podés ver el stock de cada producto en esa ubicación, derivado de los movimientos asociados a ese depósito.',
      },
      {
        q: '¿Cómo veo el historial de movimientos?',
        a: 'Andá a Stock › Movimientos. Podés filtrar por producto (buscador), tipo de movimiento (compra, venta, ajuste, merma), y rango de fechas. Cada fila muestra el stock antes y después del movimiento, el usuario que lo realizó y las notas.',
      },
    ],
  },

  // ── Tesorería ─────────────────────────────────────────────────────────────
  {
    categoria: 'Tesorería',
    preguntas: [
      {
        q: '¿Qué es una caja?',
        a: 'Una caja representa un punto físico donde se maneja dinero en efectivo (o cualquier forma de pago). Podés tener varias cajas: "Caja principal", "Caja mostrador", etc. Cada caja tiene su propio saldo calculado como la suma de ingresos menos egresos de sus movimientos.',
      },
      {
        q: '¿Cómo registro un movimiento de caja?',
        a: 'Desde Tesorería › Cajas, seleccioná la caja haciendo click en su tarjeta. Luego hacé click en "Nuevo movimiento". Elegí si es un ingreso o egreso, ingresá el monto, concepto y fecha. Los movimientos de caja son independientes de las ventas — representan el flujo real de dinero en la caja física.',
      },
      {
        q: '¿Los movimientos de caja se relacionan con las ventas?',
        a: 'No automáticamente. Las ventas registran el ingreso comercial, pero el movimiento de caja lo registrás vos manualmente cuando el dinero entra físicamente. Esto permite mayor flexibilidad en negocios con múltiples formas de cobro o liquidaciones diferidas.',
      },
      {
        q: '¿Qué es una cuenta bancaria?',
        a: 'Representa una cuenta en un banco (cuenta corriente, caja de ahorro, etc.). Se gestiona desde Tesorería › Bancos. Podés registrar el banco, nombre de cuenta, número de cuenta y CBU. Cada cuenta tiene movimientos de crédito (ingresos) y débito (egresos) con los que se calcula el saldo actual.',
      },
      {
        q: '¿Cómo filtro los movimientos por período?',
        a: 'Tanto en Cajas como en Bancos, hay filtros de fecha (desde / hasta) en la barra de filtros superior. También podés filtrar por tipo (ingreso/egreso o crédito/débito). El resumen arriba de la tabla se recalcula según el período seleccionado.',
      },
    ],
  },

  // ── Reportes ──────────────────────────────────────────────────────────────
  {
    categoria: 'Reportes',
    preguntas: [
      {
        q: '¿Qué reportes están disponibles?',
        a: 'Hay cuatro reportes: Ventas (resumen del período, ventas por día, por forma de pago, top productos y top clientes), Compras (resumen, por día, top proveedores y top productos), Stock (estado actual del inventario con valores de costo y venta), y Rentabilidad (margen de ganancia por producto en el período).',
      },
      {
        q: '¿Cómo uso los filtros de fecha?',
        a: 'En la parte superior de Reportes hay atajos de período (Hoy, Esta semana, Este mes, Último mes) y campos de fecha manual. El reporte de Stock no tiene filtro de fecha porque siempre muestra el estado actual del inventario.',
      },
      {
        q: '¿Cómo interpreto el reporte de rentabilidad?',
        a: 'Muestra por cada producto: unidades vendidas, ingresos (precio de venta × cantidad), costos (precio de costo × cantidad), ganancia (ingresos − costos) y margen porcentual. Los productos con margen negativo aparecen en rojo — significa que se vendieron por debajo del costo.',
      },
      {
        q: '¿El reporte de ventas incluye las ventas anuladas?',
        a: 'No. Todos los reportes excluyen automáticamente las ventas y compras con estado "anulada".',
      },
      {
        q: '¿Puedo exportar los reportes?',
        a: 'Por el momento los reportes son de solo lectura dentro del sistema. La exportación (PDF/Excel) está planificada para una versión futura.',
      },
    ],
  },

  // ── Configuración ─────────────────────────────────────────────────────────
  {
    categoria: 'Configuración',
    preguntas: [
      {
        q: '¿Cómo creo un nuevo usuario?',
        a: 'Andá a Configuración › Usuarios (requiere rol Administrador). Hacé click en "Nuevo usuario", completá nombre, usuario y contraseña, y elegí el rol (Administrador o Empleado). El usuario queda activo inmediatamente.',
      },
      {
        q: '¿Puedo desactivar un usuario sin eliminarlo?',
        a: 'Sí. Desde Configuración › Usuarios, usá el toggle de estado en la fila del usuario. Un usuario inactivo no puede iniciar sesión, pero sus registros históricos se conservan. No podés desactivar tu propio usuario.',
      },
      {
        q: '¿Cómo cambio la moneda del sistema?',
        a: 'Desde Configuración › Parámetros, en la sección "Moneda", seleccioná la moneda predefinida o escribí un símbolo personalizado. El símbolo se usa en todos los precios del sistema. Requiere permiso de edición de configuración.',
      },
      {
        q: '¿Para qué sirven las categorías y unidades?',
        a: 'Las categorías clasifican los productos (por ejemplo: "Pollo", "Achuras", "Bebidas"). Las unidades de medida definen cómo se cuantifican (kg, u, lt, etc.). Ambas se gestionan desde Configuración › Categorías y se asignan a cada producto en su ficha.',
      },
      {
        q: '¿Qué muestra "Información del sistema" en Parámetros?',
        a: 'Muestra datos de solo lectura: la versión del schema de base de datos, el último número correlativo de ventas y de compras. Estos valores se usan internamente para numerar documentos y no se pueden editar manualmente.',
      },
    ],
  },
];
